import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import Alert from '@spectrum-icons/workflow/Alert';
import Close from '@spectrum-icons/workflow/Close';
import styles from './DACIContactPopup.module.scss';
import primaryContactStyles from '../AddPrimaryContact/AddPrimaryContact.module.scss';
import LDAPSearchField from '../shared/LDAPSearch/LDAPSearchField';
import { resolveContactEmail } from '../../services/contactEmailUtils';
import { isContributorContactRemovalLocked } from '../../services/daciContactPermissions';
import {
  DACIRole,
  DriverRole,
  DACI_ROLES,
  DRIVER_ROLES
} from '../../constants/daciRoles';

// Add CSS import for the LDAP field styling
import 'app/components/AddPrimaryContact/AddPrimaryContact.module.scss';

// Custom Tag component to show the selected LDAP contact with a visible close button
interface ContactTagProps {
  contact: {
    name: string;
    uid?: string;
    email?: string;
  };
  onRemove: (contact: any) => void;
}

const ContactTag: React.FC<ContactTagProps> = ({ contact, onRemove }) => {
  if (!contact) return null;

  return (
    <div className={styles.contactTag}>
      <span className={styles.contactName}>{contact.name}</span>
      <button
        className={styles.contactRemoveButton}
        onClick={() => onRemove(contact)}
        aria-label={`Remove ${contact.name}`}
      >
        <Close aria-hidden="true" />
      </button>
    </div>
  );
};

export interface Contact {
  id?: string;
  name: string;
  role?: string; // Product Manager, Program Manager, etc.
  daciRole?: string; // Driver, Approver, Contributor, Informed
  imageUrl?: string;
  uid?: string;
  email?: string;
  /** LDAP/API may supply `mail` instead of `email`; merged at save via `resolveContactEmail`. */
  mail?: string;
  daciTitle?: string; // For compatibility, same as daciRole
  /** When true (API), Contributor contacts must not be removable from the popup. */
  cannotEdit?: boolean;
}

interface DACIContactPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (contact: Contact) => void;
  onDelete?: (contact: Contact) => void;
  daciRoles?: DACIRole[];
  driverRoles?: DriverRole[];
  contacts?: { id: string; name: string }[];
  existingContacts?: any[]; // Add prop for existing DACI contacts
  initialContact?: Contact; // Add prop for editing existing contact
  preSelectedRole?: string; // Add prop for pre-selecting a DACI role (Driver, Approver, Contributor, Informed)
}

const DACIContactPopup: React.FC<DACIContactPopupProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  daciRoles = DACI_ROLES,
  driverRoles = DRIVER_ROLES,
  contacts = [],
  existingContacts = [],
  initialContact,
  preSelectedRole
}) => {
  const [selectedDaciRole, setSelectedDaciRole] = useState<string>('');
  const [selectedDaciRoleName, setSelectedDaciRoleName] = useState<string>('');
  const [selectedDriverRole, setSelectedDriverRole] = useState<string>('');
  const [selectedDriverRoleName, setSelectedDriverRoleName] =
    useState<string>('');
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [contactsMarkedForDeletion, setContactsMarkedForDeletion] = useState<
    any[]
  >([]);

  // Validation: track submit attempts to show errors
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Custom dropdown states
  const [isDaciDropdownOpen, setIsDaciDropdownOpen] = useState<boolean>(false);
  const [isDriverDropdownOpen, setIsDriverDropdownOpen] =
    useState<boolean>(false);

  // Refs for click outside handling
  const daciDropdownRef = useRef<HTMLDivElement>(null);
  const driverDropdownRef = useRef<HTMLDivElement>(null);

  // Show driver role only when "Driver" is selected
  const isDriverSelected = selectedDaciRoleName === 'Driver';

  // Initialize form with initial contact data when editing
  useEffect(() => {
    if (isOpen) {
      if (initialContact) {
        // Set DACI role
        const daciRole = daciRoles.find(
          role => role.name === initialContact.daciRole
        );
        if (daciRole) {
          setSelectedDaciRole(daciRole.id);
          setSelectedDaciRoleName(daciRole.name);
        }

        // Set driver role if applicable
        if (initialContact.daciRole === 'Driver' && initialContact.role) {
          const driverRole = driverRoles.find(
            role => role.name === initialContact.role
          );

          if (driverRole) {
            setSelectedDriverRole(driverRole.id);
            setSelectedDriverRoleName(driverRole.name);
          }
        }

        // ✅ FIX: Load ALL existing contacts for the role, not just the one being edited
        // This ensures all contacts in that role are shown as chips in the edit form
        let allRoleContacts: any[] = [];

        if (initialContact.daciRole === 'Driver' && initialContact.role) {
          // For Driver role, filter by both DACI role and driver role
          allRoleContacts = existingContacts
            .filter(
              contact =>
                contact.daciRole === 'Driver' &&
                contact.role === initialContact.role
            )
            .map(contact => ({
              name: contact.name,
              uid:
                contact.uid || contact.name.toLowerCase().replace(/\s+/g, '.'),
              email: resolveContactEmail(contact),
              imageUrl: contact.imageUrl,
              type: 'ldap',
              isExisting: true, // Mark as existing
              id: contact.id, // Preserve ID for delete detection
              daciRole: contact.daciRole,
              cannotEdit: contact.cannotEdit
            }));
        } else {
          // For non-Driver roles, filter by DACI role only
          allRoleContacts = existingContacts
            .filter(contact => contact.daciRole === initialContact.daciRole)
            .map(contact => ({
              name: contact.name,
              uid:
                contact.uid || contact.name.toLowerCase().replace(/\s+/g, '.'),
              email: resolveContactEmail(contact),
              imageUrl: contact.imageUrl,
              type: 'ldap',
              isExisting: true, // Mark as existing
              id: contact.id, // Preserve ID for delete detection
              daciRole: contact.daciRole,
              cannotEdit: contact.cannotEdit
            }));
        }

        setSelectedContacts(allRoleContacts);
        setExistingRoleContacts(allRoleContacts);
      } else {
        // Reset form for new contact
        setSelectedDaciRole('');
        setSelectedDaciRoleName('');
        setSelectedDriverRole('');
        setSelectedDriverRoleName('');
        setSelectedContacts([]);
        setExistingRoleContacts([]); // Also reset existing contacts
        setHasSubmitted(false);

        // Pre-select role if provided (for quick add from role header)
        if (preSelectedRole) {
          const daciRole = daciRoles.find(
            role => role.name === preSelectedRole
          );
          if (daciRole) {
            setSelectedDaciRole(daciRole.id);
            setSelectedDaciRoleName(daciRole.name);
          }
        }
      }
    }
  }, [
    isOpen,
    initialContact,
    daciRoles,
    driverRoles,
    preSelectedRole,
    existingContacts
  ]);

  // Track existing contacts separately from newly added ones
  const [existingRoleContacts, setExistingRoleContacts] = useState<any[]>([]);

  // Populate existing contacts whenever DACI role or Driver role changes
  useEffect(() => {
    if (isOpen && selectedDaciRoleName) {
      let existing: any[] = [];

      // For Driver role, also filter by the specific driver role (PMM, PM, PGM)
      if (selectedDaciRoleName === 'Driver') {
        // Only load contacts if a driver role is selected
        if (selectedDriverRoleName) {
          existing = existingContacts
            .filter(
              contact =>
                contact.daciRole === 'Driver' &&
                contact.role === selectedDriverRoleName &&
                // ⭐ Exclude contacts marked for deletion
                !contactsMarkedForDeletion.some(
                  deletedContact =>
                    deletedContact.uid === contact.uid &&
                    deletedContact.name === contact.name
                )
            )
            .map(contact => ({
              name: contact.name,
              uid: contact.uid,
              email: resolveContactEmail(contact),
              imageUrl: contact.imageUrl,
              type: 'ldap',
              isExisting: true, // Mark as existing to avoid re-saving
              id: contact.id,
              daciRole: contact.daciRole,
              cannotEdit: contact.cannotEdit
            }));
        } else {
          // No driver role selected yet, don't load any contacts
          existing = [];
        }
      } else {
        // For non-Driver roles (Approver, Contributor, Informed), just filter by DACI role
        existing = existingContacts
          .filter(
            contact =>
              contact.daciRole === selectedDaciRoleName &&
              // ⭐ Exclude contacts marked for deletion
              !contactsMarkedForDeletion.some(
                deletedContact =>
                  deletedContact.uid === contact.uid &&
                  deletedContact.name === contact.name
              )
          )
          .map(contact => ({
            name: contact.name,
            uid: contact.uid,
            email: resolveContactEmail(contact),
            imageUrl: contact.imageUrl,
            type: 'ldap',
            isExisting: true, // Mark as existing to avoid re-saving
            id: contact.id,
            daciRole: contact.daciRole,
            cannotEdit: contact.cannotEdit
          }));
      }

      setExistingRoleContacts(existing);

      // When editing and the user changes the role, update selectedContacts to show only
      // contacts that match the NEW role selection
      if (initialContact) {
        // If we're editing and the role changed from the initial contact's role,
        // show contacts for the NEW role (which may be empty)
        const roleChanged =
          (selectedDaciRoleName === 'Driver' &&
            selectedDriverRoleName &&
            initialContact.role !== selectedDriverRoleName) ||
          (selectedDaciRoleName !== 'Driver' &&
            initialContact.daciRole !== selectedDaciRoleName);

        if (roleChanged) {
          // Role changed - show contacts for the new role (may be empty)
          // But preserve any newly added contacts that aren't saved yet
          const newlyAddedContacts = selectedContacts.filter(
            contact => !contact.isExisting
          );
          setSelectedContacts([...existing, ...newlyAddedContacts]);
        }
        // else: role hasn't changed, keep the initialContact that was set in first useEffect
      } else {
        // Not editing - show all existing contacts for this role
        // But preserve any newly added contacts that aren't saved yet
        const newlyAddedContacts = selectedContacts.filter(
          contact => !contact.isExisting
        );
        setSelectedContacts([...existing, ...newlyAddedContacts]);
      }
    } else if (isOpen && !selectedDaciRoleName) {
      // Clear contacts if no role is selected
      if (!initialContact) {
        setExistingRoleContacts([]);
        setSelectedContacts([]);
      }
    }
  }, [
    isOpen,
    selectedDaciRoleName,
    selectedDriverRoleName,
    existingContacts,
    initialContact,
    contactsMarkedForDeletion // ⭐ Re-run when contacts are marked for deletion
  ]);

  // Handle clicks outside the dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        daciDropdownRef.current &&
        !daciDropdownRef.current.contains(event.target as Node)
      ) {
        setIsDaciDropdownOpen(false);
      }
      if (
        driverDropdownRef.current &&
        !driverDropdownRef.current.contains(event.target as Node)
      ) {
        setIsDriverDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Make sure only one dropdown is open at a time
  useEffect(() => {
    if (isDaciDropdownOpen) {
      setIsDriverDropdownOpen(false);
    }
  }, [isDaciDropdownOpen]);

  useEffect(() => {
    if (isDriverDropdownOpen) {
      setIsDaciDropdownOpen(false);
    }
  }, [isDriverDropdownOpen]);

  // Reset when DACI role changes
  useEffect(() => {
    if (!isDriverSelected) {
      setSelectedDriverRole('');
      setSelectedDriverRoleName('');
    }
  }, [isDriverSelected]);

  const handleSave = () => {
    // Run validation first; highlight missing required fields on submit attempt
    const missingDaciRole = !selectedDaciRoleName;
    const missingDriverRole = isDriverSelected && !selectedDriverRoleName;
    const missingContacts =
      selectedContacts.length === 0 && contactsMarkedForDeletion.length === 0;

    if (missingDaciRole || missingDriverRole || missingContacts) {
      setHasSubmitted(true);
      return; // Do not proceed until required fields are filled
    }

    // Handle staged deletions first
    if (contactsMarkedForDeletion.length > 0 && onDelete) {
      contactsMarkedForDeletion.forEach(contactToDelete => {
        if (
          isContributorContactRemovalLocked(
            contactToDelete,
            contactToDelete.daciRole
          )
        ) {
          return;
        }
        console.log('🗑️ Processing staged deletion for:', contactToDelete);
        onDelete(contactToDelete);
      });
    }

    // Handle adds/edits (only if there are new contacts to save)
    if (onSave && selectedDaciRole && selectedContacts.length > 0) {
      const selectedDaciRoleObj = daciRoles.find(
        r => r.id === selectedDaciRole
      );

      let fullRole = '';
      let displayRole = '';

      // For Driver role, include the driver role in the saved data
      if (isDriverSelected && selectedDriverRole) {
        const selectedDriverRoleObj = driverRoles.find(
          r => r.id === selectedDriverRole
        );

        if (selectedDriverRoleObj && selectedDaciRoleObj) {
          fullRole = `${selectedDaciRoleObj.name} - ${selectedDriverRoleObj.name}`;
          displayRole = selectedDriverRoleObj.name; // Use the exact Primary Contacts role text
        }
      } else if (selectedDaciRoleObj) {
        // For non-Driver roles, just use the DACI role
        fullRole = selectedDaciRoleObj.name;
        displayRole = selectedDaciRoleObj.name;
      }

      // Only process newly added contacts (not existing ones)
      const newContactsToSave = selectedContacts.filter(
        contact => !contact.isExisting
      );

      // Process each NEW contact only
      newContactsToSave.forEach(contactToSave => {
        // Ensure we have the required fields
        if (contactToSave && contactToSave.name && contactToSave.uid) {
          // Explicitly create contact without ID to ensure it's treated as new
          const contact: Contact = {
            name: contactToSave.name,
            role: displayRole, // Use display role for UI (Product Manager, etc.)
            daciRole: selectedDaciRoleName, // Driver, Approver, Contributor, Informed
            uid: contactToSave.uid,
            email: resolveContactEmail(contactToSave),
            imageUrl: contactToSave.uid
              ? `https://s7d2.scene7.com/is/image/IMGDIR/${contactToSave.uid}`
              : 'https://via.placeholder.com/32',
            // Add the DACI title for compatibility
            daciTitle: selectedDaciRoleName
            // Explicitly NOT including 'id' field for new contacts
          };

          onSave(contact);
        }
      });
    }

    resetForm();
    onClose();
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  // handleDelete function removed - deletion now handled via chip X icons

  const resetForm = () => {
    setSelectedDaciRole('');
    setSelectedDaciRoleName('');
    setSelectedDriverRole('');
    setSelectedDriverRoleName('');
    setSelectedContacts([]);
    setExistingRoleContacts([]); // Reset existing contacts
    setContactsMarkedForDeletion([]); // Clear staged deletions
    setIsDaciDropdownOpen(false);
    setIsDriverDropdownOpen(false);
    setHasSubmitted(false);
  };

  // Custom dropdown selection handlers
  const handleDaciRoleSelect = (id: string, name: string) => {
    setSelectedDaciRole(id);
    setSelectedDaciRoleName(name);
    setIsDaciDropdownOpen(false);
  };

  const handleDriverRoleSelect = (id: string, name: string) => {
    setSelectedDriverRole(id);
    setSelectedDriverRoleName(name);
    setIsDriverDropdownOpen(false);
  };

  // Check if form is valid for enabling save button
  // Allow update if there are new contacts to add OR contacts staged for deletion
  const hasNewContacts =
    selectedDaciRole &&
    selectedContacts.length > 0 &&
    (isDriverSelected ? selectedDriverRole : true);
  const hasStagedDeletions = contactsMarkedForDeletion.length > 0;
  const isFormValid = hasNewContacts || hasStagedDeletions;

  // Field-level invalid flags (only after submit attempt)
  const isDaciRoleInvalid = hasSubmitted && !selectedDaciRoleName;
  const isDriverRoleInvalid =
    hasSubmitted && isDriverSelected && !selectedDriverRoleName;
  const isContactsInvalid =
    hasSubmitted && selectedContacts.length === 0 && !hasStagedDeletions;

  // Only render when open
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="daci-contact-title"
      data-testid="daci-contact-popup-modal-overlay"
    >
      <div
        className={styles.popupContainer}
        data-testid="daci-contact-popup-container"
      >
        <div
          className={styles.titleSection}
          data-testid="daci-contact-popup-title-section"
        >
          <h2
            id="daci-contact-title"
            className={styles.popupTitle}
            data-testid="daci-contact-popup-title"
          >
            {initialContact ? 'Edit' : 'Add DACI Contact'}
          </h2>
        </div>

        <div
          className={styles.formSection}
          data-testid="daci-contact-popup-form-section"
        >
          <div
            className={styles.formFieldGroup}
            data-testid="daci-contact-popup-daci-role-group"
          >
            <label
              className={`${styles.fieldLabel} ${styles.required}`}
              data-testid="daci-contact-popup-daci-role-label"
            >
              DACI ROLE
            </label>
            <div className={styles.dropdownContainer} ref={daciDropdownRef}>
              <div
                className={`${styles.dropdownTrigger} ${
                  isDaciRoleInvalid ? styles.invalid : ''
                }`}
                onClick={() => setIsDaciDropdownOpen(!isDaciDropdownOpen)}
                data-testid="daci-contact-popup-daci-role-trigger"
              >
                <div
                  className={styles.dropdownLabel}
                  data-testid="daci-contact-popup-daci-role-label-text"
                >
                  {selectedDaciRoleName || 'Select Role'}
                </div>
                <div
                  className={styles.iconWrapper}
                  data-testid="daci-contact-popup-daci-role-icon"
                >
                  {isDaciRoleInvalid && (
                    <Alert
                      UNSAFE_className={styles.errorIcon}
                      aria-hidden="true"
                    />
                  )}
                  <ChevronDown aria-hidden="true" />
                </div>
              </div>

              {isDaciDropdownOpen && (
                <div
                  className={styles.dropdown}
                  data-testid="daci-contact-popup-daci-role-menu"
                >
                  {daciRoles.map(role => (
                    <div
                      key={role.id}
                      className={`${styles.dropdownItem} ${
                        selectedDaciRole === role.id ? styles.selected : ''
                      }`}
                      onClick={() => handleDaciRoleSelect(role.id, role.name)}
                      data-testid={`daci-contact-popup-daci-role-option-${role.id}`}
                    >
                      {role.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {isDaciRoleInvalid && (
              <div className={styles.errorMessage}>Required Field</div>
            )}
          </div>

          {/* Only show Driver Role when "Driver" is selected */}
          {isDriverSelected && (
            <div
              className={styles.formFieldGroup}
              data-testid="daci-contact-popup-driver-role-group"
            >
              <label
                className={`${styles.fieldLabel} ${styles.required}`}
                data-testid="daci-contact-popup-driver-role-label"
              >
                DRIVER ROLE
              </label>
              <div className={styles.dropdownContainer} ref={driverDropdownRef}>
                <div
                  className={`${styles.dropdownTrigger} ${
                    isDriverRoleInvalid ? styles.invalid : ''
                  }`}
                  onClick={() => setIsDriverDropdownOpen(!isDriverDropdownOpen)}
                  data-testid="daci-contact-popup-driver-role-trigger"
                >
                  <div
                    className={styles.dropdownLabel}
                    data-testid="daci-contact-popup-driver-role-label-text"
                  >
                    {selectedDriverRoleName || 'Select Role'}
                  </div>
                  <div
                    className={styles.iconWrapper}
                    data-testid="daci-contact-popup-driver-role-icon"
                  >
                    {isDriverRoleInvalid && (
                      <Alert
                        UNSAFE_className={styles.errorIcon}
                        aria-hidden="true"
                      />
                    )}
                    <ChevronDown aria-hidden="true" />
                  </div>
                </div>

                {isDriverDropdownOpen && (
                  <div
                    className={styles.dropdown}
                    data-testid="daci-contact-popup-driver-role-menu"
                  >
                    {driverRoles.map(role => (
                      <div
                        key={role.id}
                        className={`${styles.dropdownItem} ${
                          selectedDriverRole === role.id ? styles.selected : ''
                        }`}
                        onClick={() =>
                          handleDriverRoleSelect(role.id, role.name)
                        }
                        data-testid={`daci-contact-popup-driver-role-option-${role.id}`}
                      >
                        {role.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {isDriverRoleInvalid && (
                <div className={styles.errorMessage}>Required Field</div>
              )}
            </div>
          )}

          <div
            className={styles.formFieldGroup}
            data-testid="daci-contact-popup-contact-name-group"
          >
            <label
              className={styles.fieldLabel}
              data-testid="daci-contact-popup-contact-name-label"
            >
              {selectedDaciRoleName
                ? `${selectedDaciRoleName.toUpperCase()}${
                    selectedDaciRoleName === 'Informed' ? '' : 'S'
                  } *`
                : 'CONTACT NAME *'}
            </label>
            <div
              className={styles.ldapSearchWrapper}
              data-testid="daci-contact-popup-ldap-search-wrapper"
            >
              <LDAPSearchField
                key={`daci-ldap-${selectedDaciRoleName || 'new'}`}
                label=""
                value={
                  selectedContacts.length === 1
                    ? selectedContacts[0]
                    : selectedContacts
                }
                onChange={val => {
                  // LDAP field may return a single object or array; normalize here
                  let newContacts: any[] = [];
                  if (Array.isArray(val)) {
                    newContacts = val as any[];
                  } else {
                    newContacts = val ? [val] : [];
                  }

                  // Preserve existing contacts and mark new ones as not existing
                  const updatedContacts = [
                    ...existingRoleContacts, // Keep existing contacts
                    ...newContacts
                      .filter(
                        newContact =>
                          // Only add if not already in existing contacts
                          !existingRoleContacts.some(
                            existing =>
                              existing.uid === newContact.uid &&
                              existing.name === newContact.name
                          )
                      )
                      .map(newContact => {
                        console.log(
                          '🔍 Processing new contact from LDAP:',
                          newContact
                        );
                        console.log(
                          '❓ Does new contact have ID?',
                          newContact.hasOwnProperty('id'),
                          newContact.id
                        );

                        const { id, ...contactWithoutId } = newContact; // Remove any existing ID
                        return {
                          ...contactWithoutId,
                          isExisting: false // Mark as new contact
                        };
                      })
                  ];

                  setSelectedContacts(updatedContacts);
                }}
                isRequired={true}
                isInvalid={isContactsInvalid}
                searchMode={
                  selectedDaciRoleName === 'Informed'
                    ? 'users-and-groups'
                    : 'users'
                }
                existingContacts={selectedContacts} // Pass current selectedContacts to reflect deletions
                currentDaciRole={selectedDaciRoleName}
                currentDriverRole={
                  isDriverSelected ? selectedDriverRoleName : undefined
                }
                isChipRemoveDisabled={contact =>
                  isContributorContactRemovalLocked(
                    contact,
                    selectedDaciRoleName
                  )
                }
                onDeleteExistingContact={contactToDelete => {
                  console.log(
                    '🗑️ Chip delete triggered for existing contact:',
                    contactToDelete
                  );

                  // Contributor + cannotEdit: block staging (LDAPSearchField hides remove via isChipRemoveDisabled).
                  // Stage for deletion - don't call backend until Update is clicked
                  // Find the original contact with ID from existingContacts
                  const originalContact = existingContacts.find(
                    contact =>
                      contact.uid === contactToDelete.uid &&
                      contact.name === contactToDelete.name &&
                      contact.daciRole === selectedDaciRoleName
                  );

                  const lockSource = originalContact ?? contactToDelete;
                  if (
                    isContributorContactRemovalLocked(
                      lockSource,
                      selectedDaciRoleName
                    )
                  ) {
                    return;
                  }

                  if (originalContact) {
                    console.log(
                      '🗑️ Staging contact for deletion:',
                      originalContact
                    );
                    // Add to deletion staging list
                    setContactsMarkedForDeletion(prev => [
                      ...prev,
                      originalContact
                    ]);

                    // Remove from selected contacts immediately (UI update)
                    setSelectedContacts(prev =>
                      prev.filter(
                        contact =>
                          !(
                            contact.uid === contactToDelete.uid &&
                            contact.name === contactToDelete.name
                          )
                      )
                    );

                    // Also remove from existing role contacts to prevent re-adding
                    setExistingRoleContacts(prev =>
                      prev.filter(
                        contact =>
                          !(
                            contact.uid === contactToDelete.uid &&
                            contact.name === contactToDelete.name
                          )
                      )
                    );
                  }
                }}
                cssClasses={{
                  selectedTag: primaryContactStyles.selectedTag,
                  tagContainer: primaryContactStyles.tagContainer,
                  ldapDropdown: primaryContactStyles.ldapDropdown,
                  ldapFieldContainer: primaryContactStyles.ldapFieldContainer
                }}
                data-testid="daci-contact-popup-ldap-search-field"
              />
            </div>
            {isContactsInvalid && (
              <div className={styles.errorMessage}>Required Field</div>
            )}
          </div>
        </div>

        <div
          className={primaryContactStyles.actionsContainer}
          data-testid="daci-contact-popup-actions"
        >
          {/* Delete button removed - deletion now handled via chip X icons */}

          <button
            type="button"
            className={primaryContactStyles.cancelButton}
            onClick={handleCancel}
            data-testid="daci-contact-popup-cancel-button"
          >
            <span
              className={`${primaryContactStyles.buttonText} ${primaryContactStyles.cancelButtonText}`}
            >
              Cancel
            </span>
          </button>

          <button
            type="button"
            className={primaryContactStyles.saveButton}
            onClick={handleSave}
            data-testid="daci-contact-popup-save-button"
          >
            <span
              className={`${primaryContactStyles.buttonText} ${primaryContactStyles.saveButtonText}`}
            >
              {initialContact ? 'Update' : 'Save'}
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DACIContactPopup;
